// Bildertest (NAI, 1994) — unmittelbare freie Reproduktion
// Source: NAI Bildertest_Normen.pdf (Tab. 8.4 + Tab. 8.5, p. 187)
// Looks up PR (percentile rank, "Prozentrang") for a given raw score (Wert)
// stratified by age group and setting (independent household vs. EMS).

import { makeParquetQuerier } from "./parquetQuery";

const queryParquet = makeParquetQuerier("Bildertest-NAI");

function getAgeGroup(age) {
  if (age < 55) return null;
  if (age <= 69) return "55_69";
  if (age <= 79) return "70_79";
  if (age <= 95) return "80_95";
  return null;
}

/**
 * Look up the Bildertest (NAI) PR for a given age, setting, and raw score.
 * @param {number} age
 * @param {"haushalt"|"heim"} setting - "haushalt" = Indépendant (Tab. 8.4), "heim" = EMS (Tab. 8.5)
 * @param {string|number} rawScore - integer 0–7
 * @returns {Promise<{pr: string, c: number, sw: number, iq: number} | {error: string}>}
 */
export async function calcBildertestNAI(age, setting, rawScore, lang) {
  if (age == null) {
    return { error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante" };
  }

  const ageGroup = getAgeGroup(age);
  if (!ageGroup) {
    return {
      error: lang === "de"
        ? `Alter außerhalb der Tabelle (55–95): ${age}`
        : `Âge hors tableau (55–95): ${age}`,
    };
  }

  if (setting !== "haushalt" && setting !== "heim") {
    return {
      error: lang === "de"
        ? "Setting erforderlich (Selbständig oder Heim)"
        : "Setting requis (Indépendant ou EMS)",
    };
  }

  const w = parseInt(rawScore, 10);
  if (isNaN(w)) return { error: lang === "de" ? "Ungültiger Rohwert" : "Score brut invalide" };

  const rows = await queryParquet("bildertest-nai.parquet");
  const subset = rows.filter((r) => r.setting === setting && r.age_group === ageGroup);
  if (subset.length === 0) {
    return {
      error: lang === "de"
        ? `Keine Übereinstimmung (${setting}, ${ageGroup})`
        : `Aucune correspondance (${setting}, ${ageGroup})`,
    };
  }

  const exact = subset.find((r) => r.wert === w);
  if (exact) {
    return { pr: String(exact.pr), c: exact.c, sw: exact.sw, iq: exact.iq };
  }

  const minWert = Math.min(...subset.map((r) => r.wert));
  if (w < minWert) {
    const minPr = subset.find((r) => r.wert === minWert).pr;
    return { pr: `< ${minPr}`, c: null, sw: null, iq: null };
  }

  return {
    error: lang === "de" ? `Rohwert ${w} außerhalb der Tabelle` : `Score brut ${w} hors tableau`,
  };
}
